import type { AdminPokerConfigProfile, AuthenticatedAdmin } from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type PokerOperationsResponse } from "../api/client.js";
import { can } from "../capabilities.js";
import { ApprovedConfigProfiles } from "../components/ApprovedConfigProfiles.js";
import { OperatorProfiles } from "../components/OperatorProfiles.js";
import { asAmount, asDuration, asMoment } from "../format.js";
import { pokerAlerts } from "../poker-health.js";

const REFRESH_MS = 10_000;

function snapshotAge(sampledAt: string) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(sampledAt).getTime()) / 1000));
  return seconds < 60 ? `${seconds}s old` : `${Math.floor(seconds / 60)}m old`;
}

function leaseState(expiresAtMs: number | null) {
  if (expiresAtMs === null) return "No lease";
  const seconds = Math.ceil((expiresAtMs - Date.now()) / 1000);
  return seconds > 0 ? `${seconds}s remaining` : "Expired";
}

export function PokerOperations({
  admin,
  onOpenHand,
}: {
  admin: AuthenticatedAdmin;
  onOpenHand?: (handId: string) => void;
}) {
  const [operatorProfilesEnabled, setOperatorProfilesEnabled] = useState(false);
  const [data, setData] = useState<PokerOperationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);

  const load = useCallback(async (silent = false, polling = false) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      setData(await api.pokerOperations(controller.signal, polling));
    } catch (caught) {
      if (controller.signal.aborted) return;
      setError(caught instanceof ApiError ? caught.message : "Could not load Poker operations.");
    } finally {
      if (request.current === controller) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(true, true), REFRESH_MS);
    return () => {
      window.clearInterval(interval);
      request.current?.abort();
    };
  }, [load]);

  async function activateProfile(profile: AdminPokerConfigProfile, reason: string) {
    if (!data?.table) throw new Error("Poker authority is not available.");
    const table = data.table;
    const result = await api.activatePokerConfigProfile({
      expectedActive: table.configuration.active,
      expectedPending: null,
      reason,
      tableId: table.tableId,
      target: {
        configId: profile.config.configId,
        configSha256: profile.config.configSha256,
      },
    });
    setSaved(
      result.outcome === "scheduled"
        ? `${profile.displayName} is pending the next safe table boundary.`
        : result.outcome === "already_active"
          ? `${profile.displayName} was already active.`
          : `${profile.displayName} is active.`,
    );
    await load();
  }

  async function cancelPendingProfile(reason: string) {
    if (!data?.table) throw new Error("Poker authority is not available.");
    const table = data.table;
    const pending = table.configuration.pending;
    if (!pending) throw new Error("There is no pending Poker profile to cancel.");
    await api.cancelPokerConfigProfile({
      expectedActive: table.configuration.active,
      expectedPending: pending.target,
      reason,
      tableId: table.tableId,
    });
    const pendingName =
      table.configuration.approvedProfiles.find(
        (profile) =>
          profile.config.configId === pending.target.configId &&
          profile.config.configSha256 === pending.target.configSha256,
      )?.displayName ?? pending.target.configId;
    setSaved(`${pendingName} was removed from the activation queue.`);
    await load();
  }

  if (loading && !data) return <p className="empty">Loading Poker operations…</p>;
  if (error && !data)
    return (
      <section className="body">
        <p className="banner" role="alert">
          {error}
        </p>
        <button className="btn btn-quiet" onClick={() => void load()} type="button">
          Retry
        </button>
      </section>
    );
  if (!data) return null;

  const table = data.table;
  const alerts = pokerAlerts(data);
  return (
    <section className="body poker-operations">
      <OperatorProfiles
        game="poker"
        role={admin.role}
        onEnabled={setOperatorProfilesEnabled}
        onChanged={() => load()}
      />
      <div className="poker-refresh-state" aria-live="polite">
        <span>
          Snapshot {asMoment(data.sampledAt)} · {snapshotAge(data.sampledAt)}
        </span>
        <span>{refreshing ? "Refreshing…" : "Auto-refreshes every 10 seconds"}</span>
      </div>

      {error ? (
        <div className="banner poker-alert poker-alert-high" role="alert">
          Latest refresh failed. Data may be stale. {error}
        </div>
      ) : null}
      {saved ? (
        <div className="banner banner-quiet" role="status">
          {saved}
        </div>
      ) : null}

      {alerts.map((alert) => (
        <div
          className={`banner poker-alert poker-alert-${alert.severity}`}
          key={alert.message}
          role="alert"
        >
          {alert.message}
        </div>
      ))}

      <div className="poker-status-grid">
        <article className="card poker-status-card">
          <span className="label-13 dim">Authority</span>
          <strong>{data.status}</strong>
          <small>
            {data.mode} · protocol v{data.protocolVersion}
          </small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Engine</span>
          <strong>{data.engineVersion}</strong>
          <small>
            {table ? `state revision ${table.revisions.state}` : "No active checkpoint"}
          </small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Seats</span>
          <strong>
            {table
              ? `${table.seats.filter((seat) => seat.connected).length}/${table.seats.length}`
              : "—"}
          </strong>
          <small>connected / occupied</small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Reconciliation</span>
          <strong>{table?.reconciliationDifferences.length ?? 0}</strong>
          <small>differences</small>
        </article>
      </div>

      {!table ? (
        <div className="card poker-empty">
          <strong>Poker authority is not available.</strong>
          <span className="poker-empty-copy">
            {data.enabled
              ? "The v3 checkpoint could not be read."
              : "Poker is disabled in this environment."}
          </span>
        </div>
      ) : (
        <>
          <div className="card poker-table-summary">
            <div className="card-bar">
              <strong>{table.tableId}</strong>
              <span className={`chip poker-state-${data.status}`}>{table.lifecycle}</span>
            </div>
            <dl className="poker-facts">
              <div>
                <dt>Active hand</dt>
                <dd className="mono-12">{table.activeHandId ?? "None"}</dd>
              </div>
              <div>
                <dt>Events</dt>
                <dd>{table.eventSequence}</dd>
              </div>
              <div>
                <dt>Pending intents</dt>
                <dd>{table.pendingIntents}</dd>
              </div>
              <div>
                <dt>Next wake</dt>
                <dd>
                  {table.nextWakeAtMs
                    ? asMoment(new Date(table.nextWakeAtMs).toISOString())
                    : "None"}
                </dd>
              </div>
              <div>
                <dt>Table revision</dt>
                <dd>{table.revisions.table}</dd>
              </div>
              <div>
                <dt>Hand revision</dt>
                <dd>{table.revisions.hand}</dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd>{asMoment(table.updatedAt)}</dd>
              </div>
            </dl>
            {table.reconciliationDifferences.length > 0 ? (
              <div className="banner" role="alert">
                {table.reconciliationDifferences.join(" · ")}
              </div>
            ) : null}
          </div>
          <ApprovedConfigProfiles
            operatorProfilesEnabled={operatorProfilesEnabled}
            canWrite={
              can(admin.role, "games.config.activate") ||
              (operatorProfilesEnabled && admin.role === "admin")
            }
            configuration={table.configuration}
            gameName="Poker"
            onActivate={activateProfile}
            onCancelPending={cancelPendingProfile}
            renderSettings={(profile) => (
              <>
                <div>
                  <dt>Turn</dt>
                  <dd>{asDuration(profile.config.turnDurationMs)}</dd>
                </div>
                <div>
                  <dt>Time bank</dt>
                  <dd>{asDuration(profile.config.handTimeBankMs)}</dd>
                </div>
                <div>
                  <dt>Disconnect hold</dt>
                  <dd>{asDuration(profile.config.disconnectHoldMs)}</dd>
                </div>
                <div>
                  <dt>Blinds</dt>
                  <dd>
                    {asAmount({
                      amount: profile.config.smallBlindMinor,
                      currency: profile.config.currency.code,
                    })}
                    {" / "}
                    {asAmount({
                      amount: profile.config.bigBlindMinor,
                      currency: profile.config.currency.code,
                    })}
                  </dd>
                </div>
                <div>
                  <dt>Buy-in</dt>
                  <dd>
                    {asAmount({
                      amount: profile.config.minBuyInMinor,
                      currency: profile.config.currency.code,
                    })}
                    {" – "}
                    {asAmount({
                      amount: profile.config.maxBuyInMinor,
                      currency: profile.config.currency.code,
                    })}
                  </dd>
                </div>
                <div>
                  <dt>Default buy-in</dt>
                  <dd>
                    {asAmount({
                      amount: profile.config.defaultBuyInMinor,
                      currency: profile.config.currency.code,
                    })}
                  </dd>
                </div>
                <div>
                  <dt>Buy-in step</dt>
                  <dd>
                    {asAmount({
                      amount: profile.config.buyInStepMinor,
                      currency: profile.config.currency.code,
                    })}
                  </dd>
                </div>
                <div>
                  <dt>Rake rate</dt>
                  <dd>{(profile.config.rakeBasisPoints ?? 500) / 100}%</dd>
                </div>
                <div>
                  <dt>Rake cap</dt>
                  <dd>
                    {asAmount({
                      amount: profile.config.rakeCapMinor,
                      currency: profile.config.currency.code,
                    })}
                  </dd>
                </div>
              </>
            )}
          />

          <div className="card table-scroll">
            <table className="poker-seats-table">
              <thead>
                <tr>
                  <th>Seat</th>
                  <th>Player</th>
                  <th>Stack</th>
                  <th>Connection</th>
                  <th>Lease</th>
                  <th>Next hand</th>
                </tr>
              </thead>
              <tbody>
                {table.seats.map((seat) => (
                  <tr key={seat.seatId}>
                    <td>
                      <strong>#{seat.seatIndex + 1}</strong>
                      <span className="cell-sub mono-12">{seat.seatId}</span>
                    </td>
                    <td>
                      <a href={`?screen=players&player=${encodeURIComponent(seat.userId)}`}>
                        <strong>{seat.displayName ?? "Unknown player"}</strong>
                      </a>
                      <span className="cell-sub mono-12">{seat.userId}</span>
                    </td>
                    <td>{asAmount(seat.stack)}</td>
                    <td>
                      {seat.connected ? "Connected" : "Disconnected"}
                      {seat.timedOutThisHand ? " · timed out" : ""}
                    </td>
                    <td
                      className={
                        seat.connected &&
                        seat.leaseExpiresAtMs !== null &&
                        seat.leaseExpiresAtMs <= Date.now()
                          ? "is-stale"
                          : undefined
                      }
                    >
                      {leaseState(seat.leaseExpiresAtMs)}
                    </td>
                    <td>{seat.readyForNextHand ? "Ready" : seat.lifecycle}</td>
                  </tr>
                ))}
                {table.seats.length === 0 ? (
                  <tr>
                    <td className="empty" colSpan={6}>
                      No occupied seats.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="card table-scroll">
            <div className="card-bar">
              <strong>Recent hands</strong>
              <span className="label-13 faint">Latest {table.recentHands.length}</span>
            </div>
            <table className="poker-hands-table">
              <thead>
                <tr>
                  <th>Settled</th>
                  <th>Hand</th>
                  <th>Winners</th>
                  <th>Showdown</th>
                  <th>Event</th>
                </tr>
              </thead>
              <tbody>
                {table.recentHands.map((hand) => (
                  <tr
                    aria-label={`Open poker hand ${hand.handId}`}
                    className={onOpenHand ? "clickable-row" : undefined}
                    key={hand.handId}
                    onClick={() => onOpenHand?.(hand.handId)}
                    onKeyDown={(event) => {
                      if (onOpenHand && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        onOpenHand(hand.handId);
                      }
                    }}
                    tabIndex={onOpenHand ? 0 : undefined}
                  >
                    <td>{asMoment(hand.settledAt)}</td>
                    <td className="mono-12">{hand.handId}</td>
                    <td>{hand.winnerSeatIds.join(", ") || "None"}</td>
                    <td>
                      {hand.revealedRanks
                        .map((rank) => `${rank.seatId}: ${rank.category}`)
                        .join(" · ") || "Fold"}
                    </td>
                    <td>#{hand.sequence}</td>
                  </tr>
                ))}
                {table.recentHands.length === 0 ? (
                  <tr>
                    <td className="empty" colSpan={5}>
                      No settled v3 hands yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
      <button
        className="btn btn-quiet"
        disabled={refreshing}
        onClick={() => void load(true)}
        type="button"
      >
        Refresh
      </button>
    </section>
  );
}
